import { useToast } from '../state';
import type { Toast } from '../state/toast';

/**
 * Toasts are the error surface. The server writes its `error.message` to be
 * read by a person, so it is shown verbatim rather than replaced with "something
 * went wrong"; validation issues are listed underneath, keyed by the path the
 * server named.
 */
export function Toaster(): JSX.Element {
  const { toasts, dismiss } = useToast();
  return (
    <div className="toaster" role="region" aria-label="Notifications">
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }): JSX.Element {
  const assertive = toast.kind === 'error';
  return (
    <div
      className={`toast toast--${toast.kind}`}
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
    >
      <div className="toast__body">
        <p className="toast__message">{toast.message}</p>
        {toast.detail ? <p className="toast__detail">{toast.detail}</p> : null}
        {toast.issues && toast.issues.length > 0 ? (
          <ul className="toast__issues">
            {toast.issues.map((issue, index) => (
              <li key={`${issue.path}:${issue.severity}:${index}`}>
                <code>{issue.path}</code>
                <span>{issue.message}</span>
                {issue.severity === 'warning' ? <em> (warning)</em> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <button type="button" className="toast__close" onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}